/**
 * `PublicCvCertificationDetail` — variante PUBBLICA (non autenticata) del
 * dettaglio di una certificazione (importata da Credly) associata a un profilo
 * CV esterno.
 *
 * È il pendant di `ExternalCvCertificationDetail` (area riservata) adattato al
 * contesto pubblico:
 *  - client token-less (`usePublicApiClient`): nessun Bearer, nessun OIDC;
 *  - il profilo è identificato dal Public_Token di rotta
 *    (`/public/submission/:token/certifications/:certificationId`);
 *  - a differenza del flusso interno (che ha un endpoint dedicato
 *    `certifications/:certificationId` → `MyCertificationDetailDto`), qui i dati
 *    della certificazione sono già inclusi nella dashboard pubblica
 *    (`GET /api/public/public-cv/profiles/:token/dashboard` → `certifications`,
 *    array di `ExternalCvCertificationDto`): si risolve la certificazione per
 *    `certificationId` da lì, senza un endpoint aggiuntivo. L'origine è sempre
 *    Credly nel flusso esterno, quindi non è mostrata.
 */
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { usePublicApiClient } from '../../api/usePublicApiClient';
import { ApiRequestError } from '../../api/client';
import type {
  ExternalCvCertificationDto,
  PublicExternalCvDashboardDto,
} from '../../shared/dto';
import { certExpiryStatus, formatCertDate } from '../../components/CertificationCard';
import { CertificationMedal } from '../../components/CertificationMedal';
import { Alert, Badge, Button, Icon, Loading, PageHeader } from '../../components/ui';

/** Renderizza la scadenza con lo stesso stato/tono della card certificazione. */
function ExpiryValue({ iso }: { iso: string | null }) {
  const status = certExpiryStatus(iso);
  if (status === 'none') return <>Nessuna</>;
  if (status === 'expired') {
    return <Badge tone="danger">{formatCertDate(iso)}</Badge>;
  }
  if (status === 'expiring') {
    return <Badge tone="warning">In scadenza {formatCertDate(iso)}</Badge>;
  }
  return <>{formatCertDate(iso)}</>;
}

export default function PublicCvCertificationDetail() {
  const api = usePublicApiClient();
  const navigate = useNavigate();
  const { token, certificationId } = useParams<{
    token: string;
    certificationId: string;
  }>();
  const hasToken = token !== undefined && token !== '';
  const encodedToken = encodeURIComponent(token ?? '');

  // Dashboard pubblica del profilo: include le certificazioni associate. Un
  // profilo inesistente (404) non va ritentato.
  const dashboardQuery = useQuery<PublicExternalCvDashboardDto>({
    queryKey: ['publicCv', 'profiles', 'dashboard', token],
    queryFn: () =>
      api.get<PublicExternalCvDashboardDto>(
        `/api/public/public-cv/profiles/${encodedToken}/dashboard`,
      ),
    enabled: hasToken,
    retry: false,
  });

  const certification = useMemo<ExternalCvCertificationDto | null>(
    () =>
      (dashboardQuery.data?.certifications ?? []).find(
        (c) => c.certificationId === certificationId,
      ) ?? null,
    [dashboardQuery.data, certificationId],
  );

  const backToProfile = () => {
    void navigate(`/public/submission/${encodedToken}`);
  };

  if (dashboardQuery.isLoading) {
    return <Loading>Caricamento certificazione…</Loading>;
  }

  // La dashboard pubblica è la fonte autorevole: un `404` (link non valido o
  // scaduto) o una certificazione non trovata mostrano un messaggio dedicato.
  if (
    dashboardQuery.isError ||
    dashboardQuery.data === undefined ||
    certification === null
  ) {
    const err = dashboardQuery.error;
    const notFound = err instanceof ApiRequestError && err.kind === 'not_found';
    const message = notFound
      ? 'Il profilo richiesto non esiste o il link non è più valido.'
      : dashboardQuery.data !== undefined && certification === null
        ? 'La certificazione richiesta non esiste o non è più disponibile.'
        : err instanceof Error
          ? err.message
          : 'Impossibile caricare la certificazione.';
    return (
      <section
        className="certification-detail"
        aria-labelledby="public-cv-certification-detail-title"
      >
        <PageHeader
          title="Dettaglio certificazione"
          titleId="public-cv-certification-detail-title"
          actions={
            <Button type="button" variant="ghost" onClick={backToProfile}>
              ← Torna al profilo
            </Button>
          }
        />
        <Alert tone={notFound ? 'warning' : 'error'}>{message}</Alert>
      </section>
    );
  }

  const data = certification;

  return (
    <section
      className="certification-detail"
      aria-labelledby="public-cv-certification-detail-title"
    >
      <PageHeader
        title="Dettaglio certificazione"
        titleId="public-cv-certification-detail-title"
        subtitle="I dettagli della certificazione e dell'assegnazione del profilo."
        actions={
          <Button type="button" variant="ghost" onClick={backToProfile}>
            ← Torna al profilo
          </Button>
        }
      />

      {/* Hero: medaglietta, nome, ente, skill e link alla pagina generale della
          certificazione. */}
      <div className="certification-detail__hero">
        <CertificationMedal
          imageUrl={data.imageUrl}
          name={data.name}
          issuer={data.issuer}
          backFields={[
            { label: 'Conseguita', value: formatCertDate(data.issuedAt) },
            {
              label: 'Scadenza',
              value:
                data.expiresAt === null ? 'Nessuna' : formatCertDate(data.expiresAt),
            },
          ]}
        />
        <div className="certification-detail__heading">
          <h2 className="certification-detail__name">{data.name}</h2>
          {data.issuer !== undefined && (
            <p className="certification-detail__issuer">{data.issuer}</p>
          )}
          {data.skills.length > 0 && (
            <div className="certification-detail__skills">
              {data.skills.map((skill) => (
                <Badge key={skill.id} tone="neutral">
                  {skill.name}
                </Badge>
              ))}
            </div>
          )}
          {data.url !== undefined && (
            <a
              href={data.url}
              target="_blank"
              rel="noopener noreferrer"
              className="certification-detail__link"
            >
              <Icon name="external" />
              <span>Vedi la certificazione</span>
            </a>
          )}

          {/* Assegnazione del profilo: date e link al badge specifico. */}
          <dl className="certification-detail__meta">
            <div className="certification-detail__meta-item">
              <dt>Conseguita</dt>
              <dd>{formatCertDate(data.issuedAt)}</dd>
            </div>
            <div className="certification-detail__meta-item">
              <dt>Scadenza</dt>
              <dd>
                <ExpiryValue iso={data.expiresAt} />
              </dd>
            </div>
            {data.badgeUrl !== undefined && (
              <div className="certification-detail__meta-item">
                <dt>Badge</dt>
                <dd>
                  <a
                    href={data.badgeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="certification-detail__link"
                  >
                    <Icon name="external" />
                    <span>Apri su Credly</span>
                  </a>
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>
    </section>
  );
}
